import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useTranslations } from 'hooks/useTranslations';
import { useParams } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { Grid, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInput } from 'components/molecules/FormInput';
import { FormModal } from 'components/molecules/Modal';
import { Loader } from 'components/atoms/Loader';
import { Alert } from 'components/atoms/Alert';
import { ICase, ICaseOtherCommentFormValue } from 'models/cases';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import { NoComments, StyledDetail } from '../../styles';
import { CaseCommentsAccordions } from './CaseCommentsAccordions';
import { EPermission } from 'models/permissions';
import { Pagination } from 'components/molecules/Pagination';
import { EPerPages } from 'models/table';

const initialFormValues: ICaseOtherCommentFormValue = { comment: '' };

interface IRequest {
  isAssignUser: boolean;
  caseData: ICase;
  isClosed: boolean;
  hasPermissionsOrOtherTask: boolean;
}

const Request = ({
  isAssignUser,
  caseData,
  isClosed,
  hasPermissionsOrOtherTask,
}: IRequest) => {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const { t } = useTranslations();
  const { modalAlert } = useAlert();
  const {
    getCaseOtherCommentsData,
    caseOtherCommentsData,
    paginatorOtherComments,
    addCaseOtherComment,
  } = useCase();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const schema = yup.object({
    comment: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(1000, t('general.field.validation.maxCharacters').replace('{0}', '1000'))
      .trim(),
  });

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      await getCaseOtherCommentsData(caseId, currentPage, perPage);
      setIsLoading(false);
    })();
  }, [caseId, currentPage, perPage]);

  const reloadCaseOtherComments = async () => {
    await getCaseOtherCommentsData(caseId, currentPage, perPage);
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ICaseOtherCommentFormValue>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ICaseOtherCommentFormValue>,
  });

  const handleClose = () => {
    setIsOpenModal(false);
    reset(initialFormValues);
  };

  const submitForm = async (data: ICaseOtherCommentFormValue) => {
    const res = await addCaseOtherComment(caseId, data);
    if (res) {
      handleClose();
      reloadCaseOtherComments();
    }
  };

  return (
    <>
      {isLoading && <Loader isLoading />}
      {!isLoading && (
        <Stack sx={{ gap: '20px' }}>
          <StyledDetail container size={{ xs: 12 }}>
            <Grid size={{ xs: 12 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(Elabels.taskName)}
                </Typography>
                <Typography variant="body2">{caseData.taskName}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t(Elabels.taskDescription)}
                </Typography>
                <Typography variant="body2">{caseData.taskDescription}</Typography>
              </Stack>
            </Grid>
            {caseData.summary && (
              <Grid size={{ xs: 12 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t(Elabels.taskSummary)}
                  </Typography>
                  <Typography variant="body2">{caseData.summary}</Typography>
                </Stack>
              </Grid>
            )}
          </StyledDetail>
          <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Grid>{`${t(Elabels.totalNumberOfComments)} ${paginatorOtherComments?.totalElements}`}</Grid>
            <Grid>
              {isAssignUser && !isClosed && hasPermissionsOrOtherTask && (
                <Button
                  variant={EButtonVariants.contained}
                  label={t(Elabels.addComment)}
                  size={EButtonSizes.small}
                  icon={EIconTypes.plus}
                  onClick={() => setIsOpenModal(true)}
                  id="request_add-button"
                  permission={EPermission.VERIFY_CASES}
                />
              )}
            </Grid>
          </Grid>
          {!caseOtherCommentsData?.length ? (
            <NoComments>{t('general.noCommentsAdded.label')}</NoComments>
          ) : (
            <>
              <CaseCommentsAccordions items={caseOtherCommentsData} />

              {paginatorOtherComments &&
                paginatorOtherComments?.totalElements > EPerPages.perPage25 && (
                  <Pagination
                    count={paginatorOtherComments?.totalElements}
                    perPage={perPage}
                    onChangePage={(page) => {
                      setCurrentPage(page);
                    }}
                    onChangePerPage={(value) => {
                      setPerPage(value);
                    }}
                    current={currentPage}
                    isLoading={false}
                  />
                )}
            </>
          )}

          <FormModal
            open={isOpenModal}
            onClickClose={handleClose}
            onSubmit={handleSubmit(submitForm)}
            approveButtonSubmit
            title={t(Elabels.addComment)}
            approveButtonLabel={Elabels.addComment}
            cancelButtonLabel="general.cancel.button"
            maxWidth="1200px"
            width="85%"
            titleLeft
          >
            {modalAlert && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Alert text={modalAlert.text} variant={modalAlert.variant} />
              </Box>
            )}
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <FormInput
                  name="comment"
                  label={t(Elabels.comment)}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  multiline={true}
                  minRows={5}
                  required
                  charsLimit={1000}
                  showCounter
                />
              </Grid>
            </Grid>
          </FormModal>
        </Stack>
      )}
    </>
  );
};

export default Request;
